import React, { useState, useEffect } from "react";
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { useAuth } from "../../context/AuthContext";
import { MENU } from "../../config/menu";
import logo from "../../assets/cropped-LOGO.png";
import "../../style/style.css";
import "../../components/chatwoot/chatwoot.css";
import { useChatwoot } from "../../hooks/useChatwoot";
import { useTabNotification } from "../../hooks/useTabNotification";
import { usePatchNotesStatus } from "../../hooks/usePatchNotesStatus";
import ChatwootOffcanvas from "../../components/chatwoot/ChatwootOffcanvas";
import ChatwootToast from "../../components/chatwoot/ChatwootToast";

const SidebarSection = ({ title, icon, nested, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <li className={nested ? "sidebar-subsection" : "sidebar-section"}>
      <button
        type="button"
        className={nested ? "sidebar-sublink sidebar-toggle" : "sidebar-link sidebar-toggle"}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title={title}
      >
        {icon && <i className={`bi ${icon} sidebar-icon`} />}
        <span className="sidebar-label">{title}</span>
        <span className={`sidebar-caret${open ? ' open' : ''}`}>&#8250;</span>
      </button>
      <ul className={`sidebar-submenu${open ? ' open' : ''}`}>{children}</ul>
    </li>
  );
};

// ---------------------------------------------------------------------------
// A sidebar é montada a partir de src/config/menu.js: nada de lista de papéis
// escrita aqui. Quem decide é `can(menuKey)`, que lê as permissões do banco
// (tela /manutencao/papeis) com fallback nos `roles` do próprio menu.js.
// ---------------------------------------------------------------------------

/** O nó tem algo visível para este usuário? Grupo vazio não deve aparecer. */
const temVisivel = (node, can) => {
  if (node.hidden) return false;
  if (!node.children) return can(node.key);
  // Grupo aparece se o próprio está liberado E sobrou pelo menos um filho.
  return can(node.key) && node.children.some((f) => temVisivel(f, can));
};

const MenuNode = ({ node, can, nested = false }) => {
  if (!temVisivel(node, can)) return null;

  if (node.children) {
    return (
      <SidebarSection title={node.label} icon={node.icon} nested={nested}>
        {node.children.map((filho) => (
          <MenuNode key={filho.key} node={filho} can={can} nested />
        ))}
      </SidebarSection>
    );
  }

  return (
    <li>
      <Link className={nested ? "sidebar-sublink" : "sidebar-link"} to={node.path} title={node.label}>
        {node.icon && <i className={`bi ${node.icon} sidebar-icon`} />}
        <span className="sidebar-label">{node.label}</span>
      </Link>
    </li>
  );
};

const Navbar = () => {
  const { user, can } = useAuth();
  const token = localStorage.getItem('token');
  const [chatOpen, setChatOpen] = useState(false);
  const [openConvId, setOpenConvId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop: ☰ recolhe a sidebar para um trilho de ícones (igual ao FINANCEIRO).
  // Passar o mouse expande por cima; clicar de novo volta ao normal. Escolha persiste.
  const [recolhida, setRecolhida] = useState(() => {
    try { return localStorage.getItem('sidebarRecolhida') === '1'; } catch { return false; }
  });
  const alternarRecolhida = () => setRecolhida((r) => {
    try { localStorage.setItem('sidebarRecolhida', r ? '0' : '1'); } catch { /* sem storage */ }
    return !r;
  });
  useEffect(() => {
    document.body.classList.toggle('sidebar-recolhida', recolhida);
    return () => document.body.classList.remove('sidebar-recolhida');
  }, [recolhida]);

  const {
    conversations,
    unreadCount,
    isLoading,
    hasToken,
    newMessage,
    liveConvUpdate,
    agentAvailability,
    setMyAvailability,
    refresh,
    fetchMessages,
    sendMessage,
    resolveConversation,
    markAsRead,
    fetchAgents,
    fetchAllTeams,
    assignAgent,
    assignTeam,
    dismissToast,
    chatwootBase,
    accountId,
  } = useChatwoot();

  useTabNotification(newMessage);
  const { temNovidade, esconder } = usePatchNotesStatus();

  const [showNotaModal, setShowNotaModal] = useState(false);
  const [ultimaNota, setUltimaNota] = useState(null);
  const [carregandoNota, setCarregandoNota] = useState(false);

  // Clicar no sino abre a nota mais recente direto num modal — não navega
  // mais pra /notas_atualizacao (usuário sem a permissão do menu caía numa
  // tela em branco). A lista completa continua acessível via Manutenção.
  const abrirUltimaNota = (event) => {
    event.preventDefault();
    esconder();
    setShowNotaModal(true);
    setCarregandoNota(true);
    const parsedURL = localStorage.getItem('urlParsed');
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    axios.post(`${parsedURL}post/patch-notes/marcar-visto`, {}, authHeaders).catch(() => {});
    axios.get(`${parsedURL}get/patch-notes`, authHeaders)
      .then(({ data }) => setUltimaNota((data.content || [])[0] || null))
      .catch(() => setUltimaNota(null))
      .finally(() => setCarregandoNota(false));
  };

  function handleOpenConversation(id) {
    setOpenConvId(id);
    setChatOpen(true);
  }

  function handleCloseChat() {
    setChatOpen(false);
    setOpenConvId(null);
  }

  const HandleExitSystem = async (event) => {
    event.preventDefault();
    localStorage.removeItem('token');
    localStorage.removeItem('chatwoot_availability_manual'); // permite auto-online no próximo login
    clearCache();
    window.location.href = `${window.location.origin}/login`;
    window.location.reload();
  };

  const clearCache = () => {
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }
  };

  // Fecha a sidebar no mobile ao navegar por um link
  const handleNavClick = (e) => {
    if (e.target.closest('a')) {
      setMobileOpen(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="sidebar-mobile-toggle"
        onClick={() => setMobileOpen((o) => !o)}
        aria-label="Abrir menu"
      >
        &#9776;
      </button>

      {mobileOpen && <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} />}

      <nav className={`sidebar-vertical${mobileOpen ? ' mobile-open' : ''}${recolhida ? ' recolhida' : ''}`}>
        <div className="sidebar-header">
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={alternarRecolhida}
            title={recolhida ? 'Expandir menu' : 'Recolher menu'}
            aria-label={recolhida ? 'Expandir menu' : 'Recolher menu'}
          >
            &#9776;
          </button>
          <Link to="/home" onClick={() => setMobileOpen(false)}>
            <img src={logo} alt="Sistema Operacional" className="sidebar-logo" />
          </Link>
        </div>

        <div className="sidebar-nav-scroll" onClick={handleNavClick}>
          <ul className="sidebar-menu">
            {MENU.map((node) => (
              <MenuNode key={node.key} node={node} can={can} />
            ))}
          </ul>
        </div>

        <div className="sidebar-footer">
          {(token && hasToken) && (
            <button
              type="button"
              className={`sidebar-action-btn${unreadCount > 0 ? ' has-unread' : ''}`}
              onClick={() => setChatOpen(true)}
              title="Conversas"
            >
              <i className="bi bi-chat-dots" />
              <span className="sidebar-label">Conversas</span>
              {unreadCount > 0 && (
                <span className="sidebar-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </button>
          )}

          {token && temNovidade && (
            <button
              type="button"
              className="sidebar-action-btn sidebar-update-btn"
              onClick={abrirUltimaNota}
              title="Sistema Atualizado"
            >
              <i className="bi bi-megaphone-fill"></i><span className="sidebar-label">Sistema Atualizado</span>
            </button>
          )}

          {token && user && (
            <Link to="/perfil" className="sidebar-action-btn sidebar-profile-btn" title={user.nome}>
              <span className="sidebar-label">{user.nome}</span> <i className="bi bi-person"></i>
            </Link>
          )}

          {token && (
            <button type="button" className="sidebar-action-btn sidebar-exit-btn" onClick={HandleExitSystem} title="Sair">
              <i className="bi bi-box-arrow-right"></i><span className="sidebar-label">Sair</span>
            </button>
          )}
        </div>
      </nav>

      <ChatwootOffcanvas
        isOpen={chatOpen}
        onClose={handleCloseChat}
        conversations={conversations}
        isLoading={isLoading}
        onRefresh={refresh}
        fetchMessages={fetchMessages}
        sendMessage={sendMessage}
        resolveConversation={resolveConversation}
        markAsRead={markAsRead}
        fetchAgents={fetchAgents}
        fetchAllTeams={fetchAllTeams}
        assignAgent={assignAgent}
        assignTeam={assignTeam}
        openConversationId={openConvId}
        chatwootBase={chatwootBase}
        accountId={accountId}
        agentAvailability={agentAvailability}
        onSetAvailability={setMyAvailability}
        liveConvUpdate={liveConvUpdate}
      />
      <ChatwootToast
        message={newMessage}
        onDismiss={dismissToast}
        onOpen={handleOpenConversation}
      />

      <Modal show={showNotaModal} onHide={() => setShowNotaModal(false)} centered size="lg">
        <Modal.Header closeButton className="header-main">
          <Modal.Title className="fw-bold">
            <i className="bi bi-megaphone me-2"></i>
            {ultimaNota ? `Atualização de ${ultimaNota.data_atualizacao}` : 'Sistema Atualizado'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {carregandoNota ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" />
            </div>
          ) : ultimaNota ? (
            <>
              <p className="text-muted small mb-3">
                Enviado por <strong>{ultimaNota.enviado_por || '—'}</strong>
              </p>
              <div style={{ whiteSpace: 'pre-wrap' }}>{ultimaNota.texto}</div>
            </>
          ) : (
            <div className="alert alert-secondary mb-0">
              <i className="bi bi-info-circle me-2"></i>Nenhuma nota de atualização publicada ainda.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNotaModal(false)}>Fechar</Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        :root {
          --sidebar-width: 260px;
          --sidebar-rail: 64px;
        }

        body {
          padding-left: 0;
          transition: padding-left 0.2s ease;
        }

        @media (min-width: 992px) {
          body {
            padding-left: var(--sidebar-width);
          }
          body.sidebar-recolhida {
            padding-left: var(--sidebar-rail);
          }
        }

        .sidebar-vertical {
          position: fixed;
          top: 0;
          left: 0;
          width: var(--sidebar-width);
          height: 100vh;
          background: linear-gradient(180deg, #17395e 0%, #0f263e 100%);
          display: flex;
          flex-direction: column;
          z-index: 1030;
          transform: translateX(-100%);
          transition: transform 0.25s ease;
          box-shadow: 2px 0 8px rgba(0,0,0,0.2);
        }

        .sidebar-vertical.mobile-open {
          transform: translateX(0);
        }

        @media (min-width: 992px) {
          .sidebar-vertical {
            transform: translateX(0) !important;
          }
          .sidebar-mobile-toggle,
          .sidebar-backdrop {
            display: none !important;
          }
        }

        .sidebar-mobile-toggle {
          position: fixed;
          top: 12px;
          left: 12px;
          z-index: 1040;
          background: #17395e;
          color: #fff;
          border: none;
          border-radius: 6px;
          width: 42px;
          height: 42px;
          font-size: 1.25rem;
          line-height: 1;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }

        .sidebar-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 1025;
        }

        .sidebar-header {
          padding: 1rem;
          text-align: center;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          flex-shrink: 0;
        }

        .sidebar-logo {
          width: 100%;
          max-width: 220px;
          height: auto;
        }

        .sidebar-nav-scroll {
          flex: 1 1 auto;
          overflow-y: auto;
          padding: 0.5rem 0;
          scrollbar-width: thin;
          scrollbar-color: rgba(255,255,255,0.25) transparent;
        }

        .sidebar-nav-scroll::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-nav-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .sidebar-nav-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(255,255,255,0.25);
          border-radius: 999px;
        }

        .sidebar-nav-scroll::-webkit-scrollbar-thumb:hover {
          background-color: rgba(255,255,255,0.45);
        }

        .sidebar-menu,
        .sidebar-submenu {
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .sidebar-submenu {
          background: rgba(255,255,255,0.1);
          margin-left: 1.35rem;
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height 0.25s ease, opacity 0.2s ease;
        }

        .sidebar-submenu.open {
          max-height: 1000px;
          opacity: 1;
        }

        .sidebar-submenu > li {
          position: relative;
        }

        .sidebar-submenu > li::before {
          content: "";
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          width: 1px;
          background: rgba(255,255,255,0.15);
        }

        .sidebar-submenu > li:last-child::before {
          bottom: auto;
          height: 50%;
        }

        .sidebar-submenu > li::after {
          content: "";
          position: absolute;
          top: 50%;
          left: 0;
          width: 0.9rem;
          height: 1px;
          background: rgba(255,255,255,0.15);
        }

        .sidebar-link,
        .sidebar-sublink {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          width: 100%;
          padding: 0.65rem 1rem;
          color: rgba(255,255,255,0.9);
          text-decoration: none;
          font-weight: 600;
          font-size: 0.9rem;
          background: transparent;
          border: none;
          text-align: left;
          cursor: pointer;
        }

        .sidebar-link:hover,
        .sidebar-sublink:hover {
          background: rgba(255,255,255,0.1);
          color: #fff;
        }

        .sidebar-sublink {
          font-weight: 400;
          padding-left: 0.9rem;
          font-size: 0.85rem;
        }

        .sidebar-toggle[aria-expanded="true"] {
          border-bottom: 1px solid rgba(255,255,255,0.15);
        }

        .sidebar-subsection > .sidebar-sublink {
          font-weight: 600;
        }

        .sidebar-label {
          flex: 1 1 auto;
        }

        /* Nos botões do rodapé o texto continua centralizado */
        .sidebar-action-btn .sidebar-label {
          flex: 0 1 auto;
        }

        .sidebar-profile-btn .sidebar-label {
          flex: 1 1 auto;
        }

        .sidebar-icon {
          width: 1.1rem;
          flex-shrink: 0;
          text-align: center;
          opacity: .85;
        }

        .sidebar-caret {
          transition: transform 0.2s ease;
          font-size: 1.1rem;
          flex-shrink: 0;
        }

        .sidebar-caret.open {
          transform: rotate(90deg);
        }

        .sidebar-footer {
          flex-shrink: 0;
          padding: 0.75rem;
          border-top: 1px solid rgba(255,255,255,0.1);
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .sidebar-action-btn {
          position: relative;
          width: 100%;
          padding: 0.55rem 1rem;
          border-radius: 6px;
          border: none;
          background: rgba(255,255,255,0.1);
          color: #fff;
          font-weight: 600;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          gap: 0.5rem;
        }

        .sidebar-profile-btn {
          justify-content: space-between;
        }

        .sidebar-update-btn {
          background: var(--danger, #dc3545);
          animation: sidebar-update-pulse 2s ease-in-out infinite;
        }

        .sidebar-update-btn:hover {
          background: var(--danger, #dc3545);
          filter: brightness(1.1);
        }

        @keyframes sidebar-update-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.5); }
          50% { box-shadow: 0 0 0 6px rgba(220, 53, 69, 0); }
        }

        .sidebar-action-btn:hover {
          background: rgba(255,255,255,0.2);
        }

        .sidebar-exit-btn {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.6);
        }

        .sidebar-exit-btn:hover {
          background: rgba(255,255,255,0.15);
          border-color: #fff;
        }

        .sidebar-badge {
          position: absolute;
          right: 0.6rem;
          top: 50%;
          transform: translateY(-50%);
          background: #dc3545;
          color: #fff;
          border-radius: 999px;
          font-size: 0.7rem;
          padding: 0.1rem 0.4rem;
        }

        /* Recolhida (desktop): trilho de ícones; passar o mouse expande por cima */
        .sidebar-header {
          position: relative;
        }

        .sidebar-collapse-btn {
          display: none;
          position: absolute;
          top: 10px;
          right: 10px;
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 6px;
          background: rgba(255,255,255,0.1);
          color: #fff;
          font-size: 1.1rem;
          line-height: 1;
          z-index: 1;
        }

        .sidebar-collapse-btn:hover {
          background: rgba(255,255,255,0.2);
        }

        @media (min-width: 992px) {
          .sidebar-collapse-btn { display: inline-flex; align-items: center; justify-content: center; }
          .sidebar-vertical { transition: transform 0.25s ease, width 0.2s ease; }

          .sidebar-vertical.recolhida { width: var(--sidebar-rail); }
          .sidebar-vertical.recolhida .sidebar-header { padding: 48px 8px 8px; }
          .sidebar-vertical.recolhida .sidebar-collapse-btn { right: 50%; transform: translateX(50%); }
          .sidebar-vertical.recolhida .sidebar-logo { max-width: 40px; }
          .sidebar-vertical.recolhida .sidebar-label,
          .sidebar-vertical.recolhida .sidebar-caret,
          .sidebar-vertical.recolhida .sidebar-submenu { display: none; }
          .sidebar-vertical.recolhida .sidebar-link,
          .sidebar-vertical.recolhida .sidebar-sublink { justify-content: center; padding-left: 0; padding-right: 0; }
          .sidebar-vertical.recolhida .sidebar-icon { width: auto; font-size: 1.15rem; opacity: 1; }
          .sidebar-vertical.recolhida .sidebar-action-btn { padding: 0.55rem 0; justify-content: center; }
          .sidebar-vertical.recolhida .sidebar-badge { right: 2px; top: 4px; transform: none; }

          /* Hover: volta ao tamanho cheio por cima do conteúdo (o conteúdo não se move). */
          .sidebar-vertical.recolhida:hover { width: var(--sidebar-width); box-shadow: 4px 0 16px rgba(0,0,0,0.35); }
          .sidebar-vertical.recolhida:hover .sidebar-header { padding: 1rem; }
          .sidebar-vertical.recolhida:hover .sidebar-collapse-btn { right: 10px; transform: none; }
          .sidebar-vertical.recolhida:hover .sidebar-logo { max-width: 220px; }
          .sidebar-vertical.recolhida:hover .sidebar-label,
          .sidebar-vertical.recolhida:hover .sidebar-submenu { display: block; }
          .sidebar-vertical.recolhida:hover .sidebar-caret { display: inline; }
          .sidebar-vertical.recolhida:hover .sidebar-link,
          .sidebar-vertical.recolhida:hover .sidebar-sublink { justify-content: space-between; padding-left: 1rem; padding-right: 1rem; }
          .sidebar-vertical.recolhida:hover .sidebar-sublink { padding-left: 0.9rem; }
          .sidebar-vertical.recolhida:hover .sidebar-icon { width: 1.1rem; font-size: inherit; opacity: .85; }
          .sidebar-vertical.recolhida:hover .sidebar-action-btn { padding: 0.55rem 1rem; }
          .sidebar-vertical.recolhida:hover .sidebar-profile-btn { justify-content: space-between; }
          .sidebar-vertical.recolhida:hover .sidebar-badge { right: 0.6rem; top: 50%; transform: translateY(-50%); }
        }
      `}</style>
    </>
  );
};

export default Navbar;
